import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import SearchIcon       from '@mui/icons-material/Search';
import Colors           from '../../../library/Colors';
import i18n             from '../../../i18n';

// ────────────────────────────────────────────────────────────────────────
// Webshop config search.
//
// Always-on search bar in the WebshopConfigScreen header. It jumps the user
// to any tab / sub-tab / config modal. (Previously gated behind a "click
// Shortcuts 10×" easter egg — now shown by default.)
//
// Usage in the parent screen:
//   const search = useWebshopSearch({ ...adapters });
//   <WebshopSearchBar search={search} />
//
// All admin labels MUST go through i18n (cfg_* keys) so the search index
// reads the same translations the tabs render — see the search-navigation
// project memory for context.
// ────────────────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────────────────
// Anchor pub-sub. When the user picks a search result that points at a
// specific card, we dispatch the card's anchor id here. ConfigCards that
// match the id scroll into view and briefly flash to draw attention.
//
// We also keep the most recent request as a "pending" anchor for a short
// TTL: when a search jumps to a modal, the matching ConfigCard isn't
// mounted yet at dispatch time, so it would miss the live event. By
// stashing the anchor for ~600ms, cards that mount just after the
// dispatch can still pick it up on first effect.
// ────────────────────────────────────────────────────────────────────────
const _anchorListeners = new Set();
const ANCHOR_PENDING_TTL_MS = 600;
let _pendingAnchor = null;
let _pendingAnchorAt = 0;
export const requestAnchor = (anchorId) => {
    if (!anchorId) return;
    _pendingAnchor = anchorId;
    _pendingAnchorAt = Date.now();
    _anchorListeners.forEach((fn) => fn(anchorId));
    // Auto-expire so a later, unrelated mount doesn't latch onto a stale anchor.
    setTimeout(() => {
        if (_pendingAnchor === anchorId && Date.now() - _pendingAnchorAt >= ANCHOR_PENDING_TTL_MS - 5) {
            _pendingAnchor = null;
        }
    }, ANCHOR_PENDING_TTL_MS);
};
export const useAnchor = (anchorId) => {
    const [flash, setFlash] = useState(false);
    useEffect(() => {
        if (!anchorId) return undefined;
        let timer = null;
        const trigger = () => {
            const node = document.getElementById(anchorId);
            if (node && typeof node.scrollIntoView === 'function') {
                node.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            setFlash(true);
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => setFlash(false), 1500);
        };
        const handler = (id) => { if (id === anchorId) trigger(); };
        _anchorListeners.add(handler);
        // If a request was just made (e.g. our card mounted via a modal that
        // opened in response to a search hit), claim it now.
        if (_pendingAnchor === anchorId && Date.now() - _pendingAnchorAt < ANCHOR_PENDING_TTL_MS) {
            _pendingAnchor = null;
            // Defer one tick so the DOM node is laid out before scrollIntoView.
            setTimeout(trigger, 0);
        }
        return () => {
            _anchorListeners.delete(handler);
            if (timer) clearTimeout(timer);
        };
    }, [anchorId]);
    return flash;
};

// ── Modal-internal tab pub-sub ──────────────────────────────────────────────
// Some modals/panels keep their active sub-tab in LOCAL state (carousel &
// image-grid Slides/Settings, Cadeaubon's settings/layouts/translations). A
// search hit can open the modal but couldn't pick the right inner tab. This
// pub-sub lets the search request a tab by a stable `key`; the host subscribes
// via useModalTab and sets its own state. Mirrors the anchor pub-sub, incl. a
// short "pending" window so a host that mounts JUST after the request (e.g. the
// carousel dialog that openBlockConfig just opened) still picks it up.
const MODAL_TAB_TTL_MS = 800;
let _pendingModalTab = null; // { key, tab, at }
const _modalTabListeners = new Set();
export const requestModalTab = (key, tab) => {
    if (!key || tab == null) return;
    _pendingModalTab = { key, tab, at: Date.now() };
    _modalTabListeners.forEach((fn) => fn(key, tab));
    setTimeout(() => {
        if (_pendingModalTab && Date.now() - _pendingModalTab.at >= MODAL_TAB_TTL_MS - 5) _pendingModalTab = null;
    }, MODAL_TAB_TTL_MS);
};
export const useModalTab = (key, onTab) => {
    useEffect(() => {
        if (!key || typeof onTab !== 'function') return undefined;
        // Claim a request that fired just before this host mounted.
        if (_pendingModalTab && _pendingModalTab.key === key && Date.now() - _pendingModalTab.at < MODAL_TAB_TTL_MS) {
            const { tab } = _pendingModalTab;
            _pendingModalTab = null;
            setTimeout(() => onTab(tab), 0);
        }
        const handler = (k, t) => { if (k === key) onTab(t); };
        _modalTabListeners.add(handler);
        return () => { _modalTabListeners.delete(handler); };
    }, [key, onTab]);
};

// ────────────────────────────────────────────────────────────────────────
// Prefix → location map. Used to classify EVERY `cfg_*` translation key
// into a tab / sub-tab / modal destination. Order matters: most-specific
// prefixes go first so we don't accidentally route `cfg_proc_pay_*` to
// the generic `cfg_proc_*` bucket.
//
// When a new admin section gets its own `cfg_<scope>_*` prefix, add an
// entry here so search hits route to the right place.
// ────────────────────────────────────────────────────────────────────────
const PREFIX_ROUTES = [
    // ── Processing — sub-tab specific buckets first, then catch-alls ──
    // Kasticket POS-ids live at the TOP of the Payments panel (moved there
    // from General), so they must outrank the generic cfg_proc_ bucket.
    { prefix: 'cfg_proc_receipt_pos_',  crumb: 'Processing › Payments',          cardAnchor: 'cfg-card-proc-receipt-pos', main: 3, sub: { procTab: 1 } },
    { prefix: 'cfg_proc_pay_',          crumb: 'Processing › Payments',          main: 3, sub: { procTab: 1 } },
    { prefix: 'cfg_proc_ship_pickup_',  crumb: 'Processing › Shipping & pickup', cardAnchor: 'cfg-card-proc-pickup-stores', main: 3, sub: { procTab: 2 } },
    { prefix: 'cfg_proc_ship_',         crumb: 'Processing › Shipping & pickup', main: 3, sub: { procTab: 2 } },
    { prefix: 'cfg_proc_cf_',           crumb: 'Processing › Customer fields',   main: 3, sub: { procTab: 3 } },
    { prefix: 'cfg_proc_tab_',          crumb: 'Processing',                     main: 3 },
    { prefix: 'cfg_proc_',               crumb: 'Processing › General',           main: 3, sub: { procTab: 0 } },

    // ── Sync ──
    { prefix: 'cfg_sync_imgsrc_',       crumb: 'Sync-settings › Images',         main: 4, sub: { syncTab: 2 } },
    { prefix: 'cfg_sync_imgproc_',      crumb: 'Sync-settings › Images',         main: 4, sub: { syncTab: 2 } },
    { prefix: 'cfg_sync_cat_',          crumb: 'Sync-settings › Field mapping',  main: 4, sub: { syncTab: 1 } },
    { prefix: 'cfg_sync_pfields_',      crumb: 'Sync-settings › Field mapping',  main: 4, sub: { syncTab: 1 } },
    // Product-field option labels (Barbody / Reference N / Description N).
    { prefix: 'cfg_sync_field_',        crumb: 'Sync-settings › Field mapping',  main: 4, sub: { syncTab: 1 } },
    { prefix: 'cfg_sync_lang_',         crumb: 'Sync-settings › Field mapping',  main: 4, sub: { syncTab: 1 } },
    { prefix: 'cfg_sync_tab_',          crumb: 'Sync-settings',                  main: 4 },
    { prefix: 'cfg_sync_',                crumb: 'Sync-settings › General',        main: 4, sub: { syncTab: 0 } },

    // ── Features sub-tabs ──
    // Cadeaubon: the modal has 3 sidebar sub-tabs (0=settings, 1=layouts,
    // 2=translations). Route the layout/designer/translation prefixes to their
    // sub-tab; everything else falls to the settings sub-tab. Specific first.
    { prefix: 'cfg_voucher_trans_',     crumb: 'Features ›Cadeaubon',            main: 2, sub: { featuresTab: 0 }, modalTab: { key: 'cadeaubon', tab: 2 } },
    { prefix: 'cfg_voucher_layouts_',   crumb: 'Features ›Cadeaubon',            main: 2, sub: { featuresTab: 0 }, modalTab: { key: 'cadeaubon', tab: 1 } },
    { prefix: 'cfg_voucher_designer_',  crumb: 'Features ›Cadeaubon',            main: 2, sub: { featuresTab: 0 }, modalTab: { key: 'cadeaubon', tab: 1 } },
    { prefix: 'cfg_voucher_',           crumb: 'Features ›Cadeaubon',            main: 2, sub: { featuresTab: 0 }, modalTab: { key: 'cadeaubon', tab: 0 } },
    { prefix: 'cfg_brands_',            crumb: 'Pages › Merken',                 main: 0, sub: { pagesTab: 2 } },
    { prefix: 'cfg_stl_',               crumb: 'Features ›Shop the look',        main: 2, sub: { featuresTab: 1 } },
    { prefix: 'cfg_banners_',           crumb: 'Features ›Banners',              main: 2, sub: { featuresTab: 2 } },
    { prefix: 'cfg_overige_',           crumb: 'Features ›Overige',              main: 2, sub: { featuresTab: 3 } },

    // ── Pages — static page editors and their sub-modals ──
    { prefix: 'cfg_static_about_',      crumb: 'Pages › About',                  main: 0, sub: { pagesTab: 4 } },
    { prefix: 'cfg_about_',             crumb: 'Pages › About',                  main: 0, sub: { pagesTab: 4 } },
    { prefix: 'cfg_static_disclaimer_', crumb: 'Pages › Disclaimer',             main: 0, sub: { pagesTab: 5 } },
    { prefix: 'cfg_static_privacy_',    crumb: 'Pages › Privacy',                main: 0, sub: { pagesTab: 6 } },
    { prefix: 'cfg_static_intro_',      crumb: 'Pages › Front page › Welcome',   main: 0, sub: { pagesTab: 0 } },
    { prefix: 'cfg_static_',            crumb: 'Pages › Static page',            main: 0, sub: { pagesTab: 4 } },
    { prefix: 'cfg_dyn_',               crumb: 'Pages › Dynamic page',           main: 0, sub: { pagesTab: 7 } },
    { prefix: 'cfg_container_',         crumb: 'Pages › Front page › Product container', main: 0, sub: { pagesTab: 0 } },
    // Carousel: slide fields live on the Slides tab (0), everything else on the
    // Settings tab (1) — more-specific slide prefix first.
    { prefix: 'cfg_carousel_slide',     crumb: 'Pages › Front page › Carousel',  main: 0, sub: { pagesTab: 0 }, block: 'carousel', modalTab: { key: 'carousel', tab: 0 } },
    { prefix: 'cfg_carousel_',          crumb: 'Pages › Front page › Carousel',  main: 0, sub: { pagesTab: 0 }, block: 'carousel', modalTab: { key: 'carousel', tab: 1 } },
    // Image grid: per-cell fields → Images tab (0); the rest → Settings tab (1).
    { prefix: 'cfg_imagegrid_cell',        crumb: 'Pages › Front page › Image grid', main: 0, sub: { pagesTab: 0 }, block: 'imageGrid', modalTab: { key: 'imageGrid', tab: 0 } },
    { prefix: 'cfg_imagegrid_desktop_img', crumb: 'Pages › Front page › Image grid', main: 0, sub: { pagesTab: 0 }, block: 'imageGrid', modalTab: { key: 'imageGrid', tab: 0 } },
    { prefix: 'cfg_imagegrid_alt_img',     crumb: 'Pages › Front page › Image grid', main: 0, sub: { pagesTab: 0 }, block: 'imageGrid', modalTab: { key: 'imageGrid', tab: 0 } },
    { prefix: 'cfg_imagegrid_link',        crumb: 'Pages › Front page › Image grid', main: 0, sub: { pagesTab: 0 }, block: 'imageGrid', modalTab: { key: 'imageGrid', tab: 0 } },
    { prefix: 'cfg_imagegrid_',         crumb: 'Pages › Front page › Image grid', main: 0, sub: { pagesTab: 0 }, block: 'imageGrid', modalTab: { key: 'imageGrid', tab: 1 } },
    // V2 image-grid labels (cfg_imagegridv2_*) don't match cfg_imagegrid_ (the
    // char after "cfg_imagegrid" is a 'v', not '_'), so route them explicitly.
    { prefix: 'cfg_imagegridv2_',       crumb: 'Pages › Front page › Image grid', main: 0, sub: { pagesTab: 0 }, block: 'imageGrid' },
    { prefix: 'cfg_block_',             crumb: 'Pages › Front page',             main: 0, sub: { pagesTab: 0 } },
    // Cart-page cards (CartPageTab = pagesTab 3).
    { prefix: 'cfg_cards_',             crumb: 'Pages › Cart',                   main: 0, sub: { pagesTab: 3 } },
    { prefix: 'cfg_pages_',             crumb: 'Pages',                          main: 0 },

    // ── Style modals ──
    // Card-specific buckets first, then the generic catch-alls. The
    // `cardAnchor` value identifies an individual card inside a multi-card
    // modal — the search dispatches it after the tab/modal opens so the
    // ConfigCard with that id scrolls into view and flashes.
    { prefix: 'cfg_pcard_info_',        crumb: 'Style › Product card › Information', cardAnchor: 'cfg-card-pcard-info',     main: 1, sub: { styleTab: 0 }, modal: { section: 'productCard',       mode: 'config' } },
    { prefix: 'cfg_pcard_image_',       crumb: 'Style › Product card › Image',       cardAnchor: 'cfg-card-pcard-image',    main: 1, sub: { styleTab: 0 }, modal: { section: 'productCard',       mode: 'config' } },
    { prefix: 'cfg_pcard_size_',        crumb: 'Style › Product card › Size preview',cardAnchor: 'cfg-card-pcard-size',     main: 1, sub: { styleTab: 0 }, modal: { section: 'productCard',       mode: 'config' } },
    { prefix: 'cfg_pcard_',             crumb: 'Style › Product card',                                                       main: 1, sub: { styleTab: 0 }, modal: { section: 'productCard',       mode: 'config' } },

    { prefix: 'cfg_groups_layout_',     crumb: 'Style › Filters › Layout',           cardAnchor: 'cfg-card-groups-layout',     main: 1, sub: { styleTab: 0 }, modal: { section: 'groups',            mode: 'config' } },
    { prefix: 'cfg_groups_order_',      crumb: 'Style › Filters › Filters & order',  cardAnchor: 'cfg-card-groups-order',      main: 1, sub: { styleTab: 0 }, modal: { section: 'groups',            mode: 'config' } },
    { prefix: 'cfg_groups_per_filter',  crumb: 'Style › Filters › Per-filter',       cardAnchor: 'cfg-card-groups-order',      main: 1, sub: { styleTab: 0 }, modal: { section: 'groups',            mode: 'config' } },
    { prefix: 'cfg_groups_mobile_',     crumb: 'Style › Filters › Mobile hierarchy', cardAnchor: 'cfg-card-groups-mobile',     main: 1, sub: { styleTab: 0 }, modal: { section: 'groups',            mode: 'config' } },
    { prefix: 'cfg_groups_default_',    crumb: 'Style › Filters › Default sort',     cardAnchor: 'cfg-card-groups-default',    main: 1, sub: { styleTab: 0 }, modal: { section: 'groups',            mode: 'config' } },
    { prefix: 'cfg_groups_sort_',       crumb: 'Style › Filters › Sort options',     cardAnchor: 'cfg-card-groups-sort',       main: 1, sub: { styleTab: 0 }, modal: { section: 'groups',            mode: 'config' } },
    { prefix: 'cfg_groups_',            crumb: 'Style › Filters & sorting',                                                  main: 1, sub: { styleTab: 0 }, modal: { section: 'groups',            mode: 'config' } },

    { prefix: 'cfg_pdi_',               crumb: 'Style › Product detail info',    main: 1, sub: { styleTab: 1 }, modal: { section: 'productDetailInfo', mode: 'config' } },
    { prefix: 'cfg_widths_',            crumb: 'Style › Global widths',          main: 1,                       modal: { section: 'globalWidths',      mode: 'config' } },
    { prefix: 'cfg_gallery_',           crumb: 'Style › Image gallery',          main: 1, sub: { styleTab: 1 }, modal: { section: 'image',             mode: 'config' } },
    // `cfg_extra_footer_` must be checked before `cfg_extra_` (defensive
    // ordering — `cfg_extra_` doesn't exist today but the more-specific
    // prefix should always win).
    { prefix: 'cfg_extra_footer_',      crumb: 'Style › Footer (extra)',         main: 1, sub: { styleTab: 0 }, modal: { section: 'footerExtra',       mode: 'config' } },
    { prefix: 'cfg_header_',            crumb: 'Style › Header (logo)',          main: 1, sub: { styleTab: 0 }, modal: { section: 'header',            mode: 'config' } },
    { prefix: 'cfg_footer_',            crumb: 'Style › Footer',                 main: 1, sub: { styleTab: 0 }, modal: { section: 'footer',            mode: 'config' } },
    { prefix: 'cfg_nav_sorting_',       crumb: 'Style › Menu bar › Filter order', cardAnchor: 'cfg-card-nav-sorting', main: 1, sub: { styleTab: 0 }, modal: { section: 'menu',              mode: 'config' } },
    { prefix: 'cfg_nav_',               crumb: 'Style › Menu bar',               main: 1, sub: { styleTab: 0 }, modal: { section: 'menu',              mode: 'config' } },
    { prefix: 'cfg_outlet_',            crumb: 'Style › Menu bar › Outlet mode', cardAnchor: 'cfg-card-outlet-mode', main: 1, sub: { styleTab: 0 }, modal: { section: 'menu',              mode: 'config' } },
    { prefix: 'cfg_grid_product_',      crumb: 'Style › Verkenner › Product grid',     cardAnchor: 'cfg-card-grid-product',  main: 1, sub: { styleTab: 0 }, modal: { section: 'grid',              mode: 'config' } },
    { prefix: 'cfg_grid_max_columns_',  crumb: 'Style › Verkenner › Product grid',     cardAnchor: 'cfg-card-grid-product',  main: 1, sub: { styleTab: 0 }, modal: { section: 'grid',              mode: 'config' } },
    { prefix: 'cfg_grid_pagination_',   crumb: 'Style › Verkenner › Product grid',     cardAnchor: 'cfg-card-grid-product',  main: 1, sub: { styleTab: 0 }, modal: { section: 'grid',              mode: 'config' } },
    { prefix: 'cfg_grid_show_total_',   crumb: 'Style › Verkenner › Product grid',     cardAnchor: 'cfg-card-grid-product',  main: 1, sub: { styleTab: 0 }, modal: { section: 'grid',              mode: 'config' } },
    { prefix: 'cfg_grid_variants_',     crumb: 'Style › Verkenner › Variant grouping', cardAnchor: 'cfg-card-grid-variants', main: 1, sub: { styleTab: 0 }, modal: { section: 'grid',              mode: 'config' } },
    { prefix: 'cfg_grid_group_as_one_', crumb: 'Style › Verkenner › Variant grouping', cardAnchor: 'cfg-card-grid-variants', main: 1, sub: { styleTab: 0 }, modal: { section: 'grid',              mode: 'config' } },
    { prefix: 'cfg_grid_show_swatches_',crumb: 'Style › Verkenner › Variant grouping', cardAnchor: 'cfg-card-grid-variants', main: 1, sub: { styleTab: 0 }, modal: { section: 'grid',              mode: 'config' } },
    { prefix: 'cfg_grid_max_variants_', crumb: 'Style › Verkenner › Variant grouping', cardAnchor: 'cfg-card-grid-variants', main: 1, sub: { styleTab: 0 }, modal: { section: 'grid',              mode: 'config' } },
    { prefix: 'cfg_grid_',              crumb: 'Style › Verkenner › Product grid',                                          main: 1, sub: { styleTab: 0 }, modal: { section: 'grid',              mode: 'config' } },
    // Section-specific colour fields → open that section's colour picker (mode
    // 'color') directly. The generic bg/text labels are shared across sections
    // so they stay tab-only via cfg_style_ below. More specific prefixes first.
    { prefix: 'cfg_style_color_custom_hover',    crumb: 'Style › Menu bar',     main: 1, sub: { styleTab: 0 }, modal: { section: 'menu',       mode: 'color' } },
    { prefix: 'cfg_style_color_primary_hover',   crumb: 'Style › Menu bar',     main: 1, sub: { styleTab: 0 }, modal: { section: 'menu',       mode: 'color' } },
    { prefix: 'cfg_style_color_secondary_hover', crumb: 'Style › Menu bar',     main: 1, sub: { styleTab: 0 }, modal: { section: 'menu',       mode: 'color' } },
    { prefix: 'cfg_style_color_accent',          crumb: 'Style › Menu bar',     main: 1, sub: { styleTab: 0 }, modal: { section: 'menu',       mode: 'color' } },
    { prefix: 'cfg_style_color_discount',        crumb: 'Style › Price',        main: 1, sub: { styleTab: 0 }, modal: { section: 'price',      mode: 'color' } },
    { prefix: 'cfg_style_color_border',          crumb: 'Style › Filters',      main: 1, sub: { styleTab: 0 }, modal: { section: 'sideFilter', mode: 'color' } },
    { prefix: 'cfg_style_color_active_lang',     crumb: 'Style › Language bar', main: 1, sub: { styleTab: 0 }, modal: { section: 'language',   mode: 'color' } },
    { prefix: 'cfg_style_tab_',         crumb: 'Style',                          main: 1 },
    // Generic Style-tab labels that aren't tied to one section/modal: colour
    // fields (cfg_style_color_*, shared by language/header/menu/footer), the
    // section headings (cfg_style_section_*), and the Global / Visibility panel
    // (cfg_style_global_*, cfg_style_visibility_*). Without this they fell through
    // to the catch-all below and landed on the Features tab. Keep AFTER the
    // specific cfg_pcard_/cfg_groups_/cfg_grid_/cfg_nav_/… routes (different
    // prefixes, so no conflict) and BEFORE the catch-all.
    { prefix: 'cfg_style_',             crumb: 'Style',                          main: 1 },

    // Last-resort catch-all so any forgotten cfg_* still routes somewhere.
    { prefix: 'cfg_',                   crumb: 'Webshop',                        main: 2 },
];

// Resolve a translation key to its target location, or null if the key
// doesn't start with `cfg_`.
const classifyKey = (key) => {
    for (const r of PREFIX_ROUTES) {
        if (key.startsWith(r.prefix)) return r;
    }
    return null;
};

// Breadcrumb built from the route's tab indices via the SAME i18n keys the tabs
// use, so it auto-translates (NL/FR) and stays in sync with tab renames. The
// granular 3rd segment from the old hardcoded English crumbs is dropped in
// favour of the (translated) "Main › Sub-tab" location.
const MAIN_CRUMB_KEY = ['cfg_main_pages', 'cfg_main_style', 'cfg_main_features', 'cfg_main_processing', 'cfg_main_sync'];
const SUB_CRUMB_KEY = {
    pagesTab:    ['cfg_pages_tab_front', 'cfg_pages_tab_404', 'cfg_pages_tab_merken', 'cfg_pages_tab_cart', 'cfg_pages_tab_about', 'cfg_pages_tab_disclaimer', 'cfg_pages_tab_privacy', 'cfg_pages_tab_dynamic'],
    styleTab:    ['cfg_style_tab_explorer', 'cfg_style_tab_product_detail'],
    featuresTab: ['cfg_features_tab_cadeaubon', 'cfg_features_tab_stl', 'cfg_features_tab_banners', 'cfg_features_tab_overige'],
    procTab:     ['cfg_proc_tab_general', 'cfg_proc_tab_payments', 'cfg_proc_tab_shipping', 'cfg_proc_tab_fields'],
    syncTab:     ['cfg_sync_tab_general', 'cfg_sync_tab_field', 'cfg_sync_tab_images'],
};
// The granular 3rd crumb segment (block / style section / voucher sub-tab),
// derived from the route's own fields so it stays translated. Returns an i18n
// key, or null when the location is fully described by main › sub.
const VOUCHER_SUBTAB_KEY = ['cfg_voucher_subtab_settings', 'cfg_voucher_subtab_layouts', 'cfg_voucher_subtab_trans'];
const BLOCK_TAIL_KEY = { carousel: 'cfg_block_carousel', imageGrid: 'cfg_block_image_grid' };
const tailKeyFor = (e) => {
    if (e.modalTab && e.modalTab.key === 'cadeaubon') return VOUCHER_SUBTAB_KEY[e.modalTab.tab] || null;
    if (e.block && BLOCK_TAIL_KEY[e.block]) return BLOCK_TAIL_KEY[e.block];
    if (e.modal && e.modal.section) return 'cfg_style_section_' + e.modal.section;
    return null;
};
const crumbFor = (t, e) => {
    // Only push a translated segment when the key actually resolves — i18next
    // returns the key verbatim when it's missing, so guard against a raw cfg_*
    // key leaking into the breadcrumb (every segment, not just the tail).
    const tr = (k) => (t ? t(k) : k);
    const push = (parts, key) => { if (!key) return; const v = tr(key); if (v && v !== key) parts.push(v); };
    const parts = [];
    push(parts, MAIN_CRUMB_KEY[e.main]);
    if (e.sub) {
        const [k, v] = Object.entries(e.sub)[0];
        push(parts, SUB_CRUMB_KEY[k] && SUB_CRUMB_KEY[k][v]);
    }
    // Append the granular tail (block / style section / voucher sub-tab).
    push(parts, tailKeyFor(e));
    return parts.join(' › ');
};

// Iterate over every `cfg_*` translation in the active language and turn
// each string value into a search entry. Description / help suffixes
// (`_desc`, `_help`, `_subtitle`) are kept too — they're long but contain
// keywords (e.g. "Background, text color, and size of the badge") that
// users might type. Heading / label keys without those suffixes are still
// shown front-and-centre because their values are short.
const buildFullIndex = (lang) => {
    const bundle = i18n.getResourceBundle(lang || i18n.language || 'en', 'translation') || {};
    const out = [];
    for (const [key, val] of Object.entries(bundle)) {
        if (typeof val !== 'string') continue;
        if (!key.startsWith('cfg_')) continue;
        const route = classifyKey(key);
        if (!route) continue;
        // Some editor labels are stored ALL-CAPS because the field renders them
        // through a CSS `text-transform: uppercase` wrapper (UpperLabel). In the
        // search dropdown there's no such wrapper, so the raw caps look shouty —
        // sentence-case them for display (the real i18n value is untouched, the
        // editor keeps its uppercase look).
        const isAllCaps = val === val.toUpperCase() && val !== val.toLowerCase();
        const label = isAllCaps ? (val.charAt(0) + val.slice(1).toLowerCase()) : val;
        out.push({
            id:    `tx-${key}`,            // namespace so curated ids never collide
            label,
            crumb: route.crumb,
            keywords: key,                 // also match against the technical key
            main:  route.main,
            sub:   route.sub,
            modal: route.modal,
            block: route.block,            // optional — opens a Pages block config dialog
            modalTab: route.modalTab,      // optional — selects a modal's inner sub-tab
            cardAnchor: route.cardAnchor,  // optional — flashes the matching card
        });
    }
    return out;
};

// Build the navigation index. Each entry maps a search hit to one of:
//   - main tab (number) + optional sub-tab object
//   - main tab + sub-tab + modal { section, mode }
// `keywords` is a free-form string that widens matches beyond the visible
// label — typing "vouchers" should find Cadeaubon, "ratio" should find
// Product card, etc.
const buildSearchIndex = (t) => {
    const tr = (key, fb) => (t ? t(key, fb) : fb);
    return [
        // ── Main tabs ──
        { id: 'main-features',   main: 2, label: tr('cfg_main_features',    'Features'),      crumb: tr('cfg_main_features',    'Features'),      keywords: 'features functies fonctions general algemeen général' },
        { id: 'main-pages',      main: 0, label: tr('cfg_main_pages',      'Pages'),         crumb: tr('cfg_main_pages',      'Pages'),         keywords: 'pages pagina pages' },
        { id: 'main-style',      main: 1, label: tr('cfg_main_style',      'Style'),         crumb: tr('cfg_main_style',      'Style'),         keywords: 'style stijl' },
        { id: 'main-processing', main: 3, label: tr('cfg_main_processing', 'Processing'),    crumb: tr('cfg_main_processing', 'Processing'),    keywords: 'processing orders' },
        { id: 'main-sync',       main: 4, label: tr('cfg_main_sync',       'Sync-settings'), crumb: tr('cfg_main_sync',       'Sync-settings'), keywords: 'sync synchronisation product data' },
        // Shortcuts tab is hidden for now (no data yet) — omitted from search.

        // ── Features sub-tabs ──
        { id: 'feat-cadeaubon',   main: 2, sub: { featuresTab: 0 }, label: tr('cfg_features_tab_cadeaubon', 'Cadeaubon'),     crumb: `${tr('cfg_main_features', 'Features')} › ${tr('cfg_features_tab_cadeaubon', 'Cadeaubon')}`,     keywords: 'gift voucher cadeaubon bon cadeau cheque' },
        { id: 'feat-stl',         main: 2, sub: { featuresTab: 1 }, label: tr('cfg_features_tab_stl',       'Shop the look'), crumb: `${tr('cfg_main_features', 'Features')} › ${tr('cfg_features_tab_stl',       'Shop the look')}`, keywords: 'shop the look outfit lookbook' },
        { id: 'feat-banners',     main: 2, sub: { featuresTab: 2 }, label: tr('cfg_features_tab_banners',   'Banners'),       crumb: `${tr('cfg_main_features', 'Features')} › ${tr('cfg_features_tab_banners',   'Banners')}`,       keywords: 'banner banners information' },
        { id: 'feat-overige',     main: 2, sub: { featuresTab: 3 }, label: tr('cfg_features_tab_overige',   'Overige'),       crumb: `${tr('cfg_main_features', 'Features')} › ${tr('cfg_features_tab_overige',   'Overige')}`,       keywords: 'cookies tracking analytics overige misc others' },

        // ── Pages sub-tabs ──
        { id: 'pages-front',     main: 0, sub: { pagesTab: 0 }, label: tr('cfg_pages_tab_front',      'Front page'),   crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_front',      'Front page')}`,   keywords: 'front home landing voorpagina accueil' },
        { id: 'pages-404',       main: 0, sub: { pagesTab: 1 }, label: tr('cfg_pages_tab_404',        '404 page'),     crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_404',        '404 page')}`,     keywords: 'not found 404 error' },
        { id: 'pages-merken',    main: 0, sub: { pagesTab: 2 }, label: tr('cfg_pages_tab_merken',     'Merken'),       crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_merken',     'Merken')}`,       keywords: 'brands merken marques' },
        { id: 'pages-cart',      main: 0, sub: { pagesTab: 3 }, label: tr('cfg_pages_tab_cart',       'Cart page'),    crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_cart',       'Cart page')}`,    keywords: 'cart winkelmand panier checkout' },
        { id: 'pages-about',     main: 0, sub: { pagesTab: 4 }, label: tr('cfg_pages_tab_about',      'About'),        crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_about',      'About')}`,        keywords: 'about over ons à propos' },
        { id: 'pages-disclaim',  main: 0, sub: { pagesTab: 5 }, label: tr('cfg_pages_tab_disclaimer', 'Terms & conditions'),   crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_disclaimer', 'Terms & conditions')}`,   keywords: 'disclaimer legal mentions terms conditions algemene voorwaarden conditions générales' },
        { id: 'pages-privacy',   main: 0, sub: { pagesTab: 6 }, label: tr('cfg_pages_tab_privacy',    'Privacy'),      crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_privacy',    'Privacy')}`,      keywords: 'privacy gdpr confidentialité' },
        { id: 'pages-dynamic',   main: 0, sub: { pagesTab: 7 }, label: tr('cfg_pages_tab_dynamic',    'Dynamic page'), crumb: `${tr('cfg_main_pages', 'Pages')} › ${tr('cfg_pages_tab_dynamic',    'Dynamic page')}`, keywords: "dynamic dynamische pagina's" },

        // ── Style sub-tabs ──
        { id: 'style-explorer',  main: 1, sub: { styleTab: 0 }, label: tr('cfg_style_tab_explorer',       'Verkenner'),       crumb: 'Style › Verkenner',       keywords: 'explorer verkenner explore listing grid' },
        { id: 'style-pdetail',   main: 1, sub: { styleTab: 1 }, label: tr('cfg_style_tab_product_detail', 'Product detail'),  crumb: 'Style › Product detail',  keywords: 'product detail fiche productdetail' },

        // ── Style → individual config modals (jump straight in) ──
        // Shared modals default to Verkenner since the edited state applies
        // to both Style sub-tabs.
        { id: 'modal-language',     main: 1, sub: { styleTab: 0 }, modal: { section: 'language',          mode: 'config' }, label: 'Language bar',                       crumb: 'Style › Language bar',                  keywords: 'language taalbalk languages flags switcher' },
        { id: 'modal-header',       main: 1, sub: { styleTab: 0 }, modal: { section: 'header',           mode: 'config' }, label: 'Header (logo)',                      crumb: 'Style › Header (logo)',                 keywords: 'header logo brand banner top' },
        { id: 'modal-menu',         main: 1, sub: { styleTab: 0 }, modal: { section: 'menu',             mode: 'config' }, label: 'Menu bar',                           crumb: 'Style › Menu bar',                      keywords: 'menu navigation nav navigatiebalk navigatie' },
        { id: 'modal-footer',       main: 1, sub: { styleTab: 0 }, modal: { section: 'footer',           mode: 'config' }, label: 'Footer',                             crumb: 'Style › Footer',                        keywords: 'footer voetbalk' },
        { id: 'modal-footer-extra', main: 1, sub: { styleTab: 0 }, modal: { section: 'footerExtra',      mode: 'config' }, label: 'Footer (extra)',                     crumb: 'Style › Footer (extra)',                keywords: 'footer extra payment methods extra onderbalk' },
        { id: 'modal-widths',       main: 1, sub: { styleTab: 0 }, modal: { section: 'globalWidths',     mode: 'config' }, label: 'Global width settings',              crumb: 'Style › Global width settings',         keywords: 'width breedte container max-width' },
        { id: 'modal-groups',       main: 1, sub: { styleTab: 0 }, modal: { section: 'groups',           mode: 'config' }, label: 'Filters and sorting',                crumb: 'Style › Verkenner › Filters and sorting',  keywords: 'filters sortering groepen filter sorting groups categories' },
        { id: 'modal-pcard',        main: 1, sub: { styleTab: 0 }, modal: { section: 'productCard',      mode: 'config' }, label: 'Product information (Verkenner)',     crumb: 'Style › Verkenner › Product information', keywords: 'product card information lines image fit aspect ratio size preview hover' },
        { id: 'modal-pdi',          main: 1, sub: { styleTab: 1 }, modal: { section: 'productDetailInfo', mode: 'config' }, label: 'Product detail information',         crumb: 'Style › Product detail › Product detail information', keywords: 'product detail information layout sections breadcrumb related stock' },
        { id: 'modal-image',        main: 1, sub: { styleTab: 1 }, modal: { section: 'image',            mode: 'config' }, label: 'Image gallery',                      crumb: 'Style › Product detail › Image gallery', keywords: 'image gallery product image thumbnail aspect arrows fullscreen' },

        // ── Processing sub-tabs ──
        { id: 'proc-general',    main: 3, sub: { procTab: 0 }, label: tr('cfg_proc_tab_general',  'General'),            crumb: 'Processing › General',          keywords: 'b2b warehouse discount cart registration tickets external stock' },
        { id: 'proc-payments',   main: 3, sub: { procTab: 1 }, label: tr('cfg_proc_tab_payments', 'Payments'),           crumb: 'Processing › Payments',         keywords: 'payment betaling paiement msp multisafepay invoice' },
        { id: 'proc-shipping',   main: 3, sub: { procTab: 2 }, label: tr('cfg_proc_tab_shipping', 'Shipping & pickup'),  crumb: 'Processing › Shipping & pickup', keywords: 'shipping verzending livraison pickup afhaling delivery' },
        { id: 'proc-fields',     main: 3, sub: { procTab: 3 }, label: tr('cfg_proc_tab_fields',   'Customer fields'),    crumb: 'Processing › Customer fields',  keywords: 'customer fields klantvelden' },

        // ── Sync sub-tabs ──
        { id: 'sync-general',    main: 4, sub: { syncTab: 0 }, label: tr('cfg_sync_tab_general', 'General'),             crumb: 'Sync-settings › General',       keywords: 'source webshop stores stock filtering' },
        { id: 'sync-field',      main: 4, sub: { syncTab: 1 }, label: tr('cfg_sync_tab_field',   'Field mapping'),       crumb: 'Sync-settings › Field mapping', keywords: 'field mapping veld category language' },
        { id: 'sync-images',     main: 4, sub: { syncTab: 2 }, label: tr('cfg_sync_tab_images',  'Images'),              crumb: 'Sync-settings › Images',        keywords: 'images afbeeldingen image processing v2' },
    ];
};

/**
 * Hook holding all search state + behaviour. Adapters (setTabValue, setXxxTab,
 * openStyleModal, notify) come from the parent screen so the search stays
 * decoupled from React-Router and the screen's own local state.
 */
export const useWebshopSearch = ({
    t, theme, notify,
    setTabValue,
    setFeaturesTab, setPagesTab, setStyleTab, setProcTab, setSyncTab,
    openStyleModal, openBlockConfig,
} = {}) => {
    // Search is always available now — the bar shows in the header by
    // default (the old "click Shortcuts 10×" easter-egg unlock is gone).
    const [searchEnabled]                     = useState(true);
    const [searchQuery,    setSearchQuery]    = useState('');
    const [searchOpen,     setSearchOpen]     = useState(false);

    // Track the active language so the full-text index rebuilds when the
    // user switches NL/EN/FR. `t` is a stable reference per call site, so
    // we can't depend on it alone.
    const [lang, setLang] = useState(() => i18n.language || 'en');
    useEffect(() => {
        const handler = (l) => setLang(l);
        i18n.on('languageChanged', handler);
        return () => i18n.off('languageChanged', handler);
    }, []);

    // Curated index: the high-value navigation targets (tabs, sub-tabs,
    // modal openers). Always shown first in results.
    const curatedIndex = useMemo(() => buildSearchIndex(t), [t]);

    // Full-text index: every `cfg_*` translation. Rebuilt when the language
    // changes. May be huge (200+ entries) — that's why we filter strictly
    // and cap visible matches.
    const fullIndex = useMemo(() => buildFullIndex(lang), [lang]);

    const searchMatches = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return [];

        const matches = (it) => {
            const haystack = `${it.label} ${it.crumb} ${it.keywords || ''}`.toLowerCase();
            return haystack.includes(q);
        };

        // Curated first — they map to navigation targets and tend to be what
        // the user actually wants. Cap to 4 so they don't drown out deeper
        // translation hits.
        const curatedHits = curatedIndex.filter(matches).slice(0, 4);

        // Full-text hits.
        const fullHits = fullIndex.filter(matches);

        // Rank full-text: prefix matches (label starts with q) above substring.
        fullHits.sort((a, b) => {
            const ap = a.label.toLowerCase().startsWith(q) ? 0 : 1;
            const bp = b.label.toLowerCase().startsWith(q) ? 0 : 1;
            return ap - bp;
        });

        // Combine + dedupe. Many `cfg_*` keys translate to the same value
        // (e.g. "Brand" lives in product card config, filter list, details
        // list), and curated entries can also collide with full-text rows
        // for the same nav target. Use `label + crumb` as the signature:
        // two rows with the same label that lead to different sections are
        // legitimate hits and stay; identical label+crumb rows collapse.
        const seen = new Set();
        const out  = [];
        for (const e of [...curatedHits, ...fullHits]) {
            const sig = `${e.label.toLowerCase()}::${e.crumb.toLowerCase()}`;
            if (seen.has(sig)) continue;
            seen.add(sig);
            // Translated breadcrumb for display (matching/dedup still use the
            // stable English `crumb`); fall back to it if derivation is empty.
            out.push({ ...e, displayCrumb: crumbFor(t, e) || e.crumb });
            if (out.length >= 12) break;
        }
        return out;
    }, [searchQuery, curatedIndex, fullIndex]);

    const jumpTo = (entry) => {
        if (setTabValue) setTabValue(entry.main);
        if (entry.sub) {
            if ('featuresTab' in entry.sub && setFeaturesTab) setFeaturesTab(entry.sub.featuresTab);
            if ('pagesTab'   in entry.sub && setPagesTab)   setPagesTab(entry.sub.pagesTab);
            if ('styleTab'   in entry.sub && setStyleTab)   setStyleTab(entry.sub.styleTab);
            if ('procTab'    in entry.sub && setProcTab)    setProcTab(entry.sub.procTab);
            if ('syncTab'    in entry.sub && setSyncTab)    setSyncTab(entry.sub.syncTab);
        }
        // Tab switch flushes first, then the modal opens against the new tree.
        if (entry.modal && openStyleModal) {
            setTimeout(() => openStyleModal(entry.modal.section, entry.modal.mode), 0);
        }
        // Pages block configs (carousel / image grid) open via their own gear
        // dialog (setConfigBlock), not openStyleModal — fire after the tab switch.
        if (entry.block && openBlockConfig) {
            setTimeout(() => openBlockConfig(entry.block), 0);
        }
        // Select the right inner sub-tab of the opened modal/panel (carousel &
        // image-grid Slides/Settings, Cadeaubon sub-tabs). The host claims this
        // via useModalTab — pending window covers a modal that mounts just after.
        if (entry.modalTab) {
            setTimeout(() => requestModalTab(entry.modalTab.key, entry.modalTab.tab), 0);
        }
        // Card-level anchor: scroll the matching ConfigCard into view and
        // flash it. Wait two ticks — first for the tab switch render, second
        // so that any modal that just opened has its cards mounted.
        if (entry.cardAnchor) {
            setTimeout(() => requestAnchor(entry.cardAnchor), entry.modal ? 60 : 0);
        }
        setSearchQuery('');
        setSearchOpen(false);
    };

    return {
        searchEnabled,
        searchQuery,
        searchOpen,
        searchMatches,
        setSearchQuery,
        setSearchOpen,
        jumpTo,
    };
};

/**
 * Visual search bar — TextField + dropdown of matches. Renders nothing
 * until `search.searchEnabled` is true (the easter egg gate).
 *
 * Keyboard:
 *   ↓ / ↑    move highlight through the result list
 *   Enter    jump to the highlighted result (or the first one if none yet)
 *   Tab      same as Enter — keeps the eg. spotlight-feel; also stops the
 *            browser from tabbing past the (otherwise useless) input
 *   Esc      close the popover without navigating
 */
export const WebshopSearchBar = ({ search }) => {
    const [highlight, setHighlight] = useState(0);

    // Refs to each result row so we can scroll the active one into view
    // when the highlight moves past the visible area of the popover.
    const rowRefs = useRef([]);

    // Reset the highlight to the top whenever the result list changes
    // (new query, new matches, popover re-opened).
    useEffect(() => { setHighlight(0); }, [search?.searchQuery, search?.searchMatches?.length]);

    // Bring the highlighted row into view. `block: 'nearest'` only scrolls
    // when the row is actually outside the visible area, so the popover
    // doesn't jiggle for already-visible items.
    useEffect(() => {
        const node = rowRefs.current[highlight];
        if (node && typeof node.scrollIntoView === 'function') {
            node.scrollIntoView({ block: 'nearest' });
        }
    }, [highlight]);

    if (!search?.searchEnabled) return null;

    const { searchQuery, setSearchQuery, searchOpen, setSearchOpen, searchMatches, jumpTo } = search;
    const lastIdx = Math.max(0, searchMatches.length - 1);

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSearchOpen(true);
            setHighlight((h) => Math.min(lastIdx, h + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSearchOpen(true);
            setHighlight((h) => Math.max(0, h - 1));
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            if (searchMatches.length === 0) return;
            // Tab default would move focus away — block that so the user
            // stays in the search flow.
            e.preventDefault();
            jumpTo(searchMatches[Math.min(highlight, lastIdx)]);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setSearchOpen(false);
        }
    };

    return (
        <Box sx={{ position: 'relative', flexShrink: 0, py: 1 }}>
            <TextField
                size="small"
                placeholder="Search settings…"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                onKeyDown={onKeyDown}
                onFocus={() => setSearchOpen(true)}
                // Small delay so a click on a result lands BEFORE the popover
                // closes from the blur. 150ms is long enough for the click
                // event to register, short enough to feel instant.
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: Colors.sft_blue }} /></InputAdornment>,
                }}
                sx={{ width: 260 }}
            />
            {searchOpen && searchMatches.length > 0 && (
                <Paper
                    elevation={4}
                    sx={{
                        position: 'absolute', top: '100%', right: 0, mt: 0.5,
                        width: 320, zIndex: 50,
                        maxHeight: 360, overflowY: 'auto',
                        borderRadius: 1.5,
                    }}
                >
                    {searchMatches.map((m, i) => {
                        const isActive = i === Math.min(highlight, lastIdx);
                        return (
                            <Box
                                key={m.id}
                                ref={(el) => { rowRefs.current[i] = el; }}
                                // mouseDown + preventDefault so the input's blur
                                // doesn't fire and close the popover before the
                                // click lands.
                                onMouseDown={(e) => { e.preventDefault(); jumpTo(m); }}
                                onMouseEnter={() => setHighlight(i)}
                                sx={{
                                    px: 1.5, py: 1,
                                    cursor: 'pointer',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    '&:last-child': { borderBottom: 'none' },
                                    backgroundColor: isActive ? 'rgba(25,118,210,0.10)' : 'transparent',
                                    borderLeft: '3px solid',
                                    borderLeftColor: isActive ? 'primary.main' : 'transparent',
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {m.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                    {m.displayCrumb || m.crumb}
                                </Typography>
                            </Box>
                        );
                    })}
                </Paper>
            )}
            {searchOpen && searchQuery.trim() && searchMatches.length === 0 && (
                <Paper
                    elevation={4}
                    sx={{
                        position: 'absolute', top: '100%', right: 0, mt: 0.5,
                        width: 320, zIndex: 50,
                        borderRadius: 1.5,
                        px: 1.5, py: 1.25,
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        No matches for &quot;{searchQuery}&quot;
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

WebshopSearchBar.propTypes = {
    search: PropTypes.shape({
        searchEnabled:  PropTypes.bool,
        searchQuery:    PropTypes.string,
        searchOpen:     PropTypes.bool,
        searchMatches:  PropTypes.array,
        setSearchQuery: PropTypes.func,
        setSearchOpen:  PropTypes.func,
        jumpTo:         PropTypes.func,
    }),
};
